import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

const NotFoundPremium = () => {
  const seoData = {
    title: "Page Not Found - Livneu",
    description:
      "The page you're looking for doesn't exist. Return to Livneu's homepage to explore our VTuber agency services.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/404",
    keywords: "404 error, page not found, Livneu",
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-hero">
        <SEOHead seoData={seoData} />
        
        {/* Kawaii floating elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-pastel-purple/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-pastel-blue/15 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="text-center max-w-4xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* 404 Number with Kawaii Glow Effect */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-8"
            >
              <h1 className="text-8xl md:text-9xl lg:text-[12rem] font-title text-text-accent leading-none kawaii-glow">
                404
              </h1>
              <div className="w-32 h-1 bg-gradient-button mx-auto rounded-full"></div>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-4xl md:text-5xl font-title text-text-primary mb-6"
            >
              Page Not Found
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-xl md:text-2xl text-text-secondary mb-12 leading-relaxed max-w-2xl mx-auto font-body"
            >
              Looks like this virtual space doesn't exist yet. Let's get you
              back to the main stage where the real magic happens.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <Link to="/">
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 16px 32px rgba(200, 162, 255, 0.3)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-button hover:bg-gradient-button-hover text-black font-accent px-8 py-4 text-lg rounded-full shadow-button transition-all duration-300"
                >
                  Return Home
                </motion.button>
              </Link>

              <Link to="/about">
                <motion.button
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 12px 24px rgba(162, 194, 255, 0.2)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="glass-card px-8 py-4 text-text-primary font-accent text-lg rounded-full border-pastel-purple/20 hover:border-pastel-blue/40 transition-all duration-300"
                >
                  Learn About Us
                </motion.button>
              </Link>
            </motion.div>

            {/* Additional Navigation Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
              className="mt-16 pt-8 border-t border-white/20"
            >
              <p
                className="text-gray-400 mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Or explore these popular pages:
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                {[
                  {
                    name: "Apply as Talent",
                    path: "/apply",
                    icon: "star-outline",
                  },
                  {
                    name: "Contact Us",
                    path: "/contact",
                    icon: "mail-outline",
                  },
                  {
                    name: "Join Discord",
                    path: "https://discord.com/invite/xauShxdx6u",
                    icon: "logo-discord",
                    external: true,
                  },
                ].map((link, index) => (
                  <motion.div
                    key={link.name}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-block"
                  >
                    {link.external ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-6 py-3 glass-card text-gray-700 rounded-xl border-white/10 hover:border-purple-400/30 hover:text-purple-400 transition-all duration-300 no-underline"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <ion-icon
                          name={link.icon}
                          style={{ fontSize: "1.2rem" }}
                        ></ion-icon>
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        className="flex items-center gap-2 px-6 py-3 glass-card text-gray-700 rounded-xl border-white/10 hover:border-purple-400/30 hover:text-purple-400 transition-all duration-300 no-underline"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <ion-icon
                          name={link.icon}
                          style={{ fontSize: "1.2rem" }}
                        ></ion-icon>
                        {link.name}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Fun Easter Egg */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="mt-12"
            >
              <p
                className="text-gray-500 text-sm"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Fun fact: Even virtual stars sometimes take wrong turns in
                cyberspace!
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default NotFoundPremium;
